import cx from "clsx";
import { navigate } from "gatsby";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import YouTubeLite from "../../../components/YouTubeLite/YouTubeLite";
import CurvedBorderCircularDeep from "../../../components/borders/CurvedBorderCircularDeep";
import FuzzyBorder from "../../../components/borders/FuzzyBorder";
import FuzzyBorderFullWidth from "../../../components/borders/FuzzyBorderFullWidth";
import { Button } from "../../../components/common";
import ProcessedImage from "../../../components/common/ProcessedImage/ProcessedImage";
import type { QuickLinksItemProps } from "../../../components/common/QuickLinksItem";
import useWindowWidth from "../../../hooks/useWindowWidth";
import { Colours } from "../../../utils/colours";
import { pushToDataLayer } from "../../../utils/google";
import { getSupportWidgetScript } from "../../../utils/scripts";
import * as styles from "./IntroHomepageComponent.module.scss";

export type ContentVariant = "links" | "widget";
export type MediaVariant = "image" | "video";
export interface IntroHomepageComponentProps {
	title: string;
	text: string;
	imageSrc?: string;
	imageAlt?: string;
	ctaTitle?: string;
	ctaUrl?: string;
	widgetTheme?: string;
	sombreBannerTheme?: boolean;
	embedCode?: string;
	videoId?: string;
	contentVariant?: ContentVariant;
	mediaVariant?: MediaVariant;
	linkItems?: QuickLinksItemProps[];
}

const QuickLinksEmbeddedItem: React.FC<QuickLinksItemProps> = ({
	title,
	imgSrc,
	imgAlt,
	url,
}) => {
	return (
		<div className={styles.embeddedquicklinkitem}>
			<button
				type="button"
				className={styles.iconwrapper}
				tabIndex={0}
				onClick={(e) => {
					e.preventDefault();
					pushToDataLayer({
						event: "button_navigation",
						button_clicked: title,
					});
					navigate(url);
				}}
				onKeyDown={(e) => {
					if (e.key === "Enter") navigate(url);
				}}
			>
				<ProcessedImage
					src={imgSrc}
					dimensions="80x80"
					width={80}
					height={80}
					alt={imgAlt}
				/>
			</button>
			<p className={styles.quicklinkstitle}>{title}</p>
		</div>
	);
};

export const IntroHomepageComponent: React.FC<IntroHomepageComponentProps> = ({
	title,
	text,
	imageSrc,
	imageAlt,
	ctaTitle,
	ctaUrl,
	widgetTheme,
	sombreBannerTheme,
	embedCode,
	videoId,
	contentVariant,
	mediaVariant,
	linkItems,
}) => {
	const [hasCalledIframeResizer, setHasCalledIframeResizer] = useState(false);
	useEffect(() => {
		const callIframeResizer = () => {
			if (hasCalledIframeResizer) return;
			if (window.iFrameResize) {
				window.iFrameResize.call();
				setHasCalledIframeResizer(true);
			}
		};
		let interval: NodeJS.Timeout = null;
		if (!hasCalledIframeResizer) {
			interval = setInterval(callIframeResizer, 500);
		} else {
			clearInterval(interval);
		}
		return () => clearInterval(interval);
	}, [hasCalledIframeResizer]);

	const windowWidth = useWindowWidth();

	const Widget = useCallback(
		() => (
			<div
				className={
					widgetTheme
						? styles[`widgetouter${widgetTheme}` as keyof typeof styles]
						: styles.widget
				}
				// biome-ignore lint/security/noDangerouslySetInnerHtml: Safe source
				dangerouslySetInnerHTML={{
					__html: getSupportWidgetScript(embedCode),
				}}
			/>
		),
		[widgetTheme, embedCode],
	);
	const DesktopWidget = useMemo(() => {
		if (windowWidth >= 1024) return <Widget />;
		return null;
	}, [Widget, windowWidth]);

	const MobileWidget = useMemo(() => {
		if (windowWidth < 1024) return <Widget />;
		return null;
	}, [Widget, windowWidth]);
	return (
		<>
			<div className={styles.mobile}>
				<div
					className={cx(styles.herowithimagecontainerouter, {
						[styles.sombreTheme]: sombreBannerTheme,
					})}
				>
					{mediaVariant === "image" && (
						<div
							className={cx(styles.herowithimagecontainer, {
								[styles.sombreTheme]: sombreBannerTheme,
							})}
						>
							<ProcessedImage
								src={imageSrc}
								alt={imageAlt}
								dimensions="400x300"
								loading="eager"
							/>
							<div className={styles.bordersection}>
								<FuzzyBorder
									className={styles.border}
									rotation={0}
									fill={
										sombreBannerTheme
											? Colours.MIDNIGHT
											: Colours.SUNSHINE_YELLOW
									}
								/>
							</div>
						</div>
					)}
					{mediaVariant === "video" && (
						<div
							className={cx(styles.herowithvideocontainer, {
								[styles.sombreTheme]: sombreBannerTheme,
							})}
						>
							<YouTubeLite
								title={`${title} introductory video`}
								youtubeId={videoId}
								poster={"maxresdefault"}
							/>
						</div>
					)}

					<div
						className={
							contentVariant ? styles.introcontent : styles.introcontentjusttext
						}
					>
						<header className={styles.textcontainer}>
							<h1>{title}</h1>
							<p>{text}</p>
							{ctaTitle && ctaUrl && (
								<Button
									className={styles.cta}
									url={ctaUrl}
									variant={sombreBannerTheme ? "reversetertiary" : "secondary"}
								>
									{ctaTitle}
								</Button>
							)}
						</header>
						{contentVariant === "links" && (
							<div className={styles.linkscontainer}>
								{linkItems.map((quickLinks) => (
									<QuickLinksEmbeddedItem key={quickLinks.title} {...quickLinks} />
								))}
							</div>
						)}
						{contentVariant === "widget" && MobileWidget}
					</div>
				</div>
			</div>

			<div className={styles.desktop}>
				<div
					className={cx(styles.herowithimagecontainerouter, {
						[styles.sombreTheme]: sombreBannerTheme,
					})}
				>
					<div className={styles.herowithimagecontainerinner}>
						{mediaVariant === "image" && (
							<div
								className={cx(styles.herowithimagecontainer, {
									[styles.sombreTheme]: sombreBannerTheme,
								})}
							>
								<ProcessedImage
									src={imageSrc}
									alt={imageAlt}
									dimensions="600x450"
									width={600}
									height={450}
									loading="eager"
								/>
								<div className={styles.bordersection}>
									<CurvedBorderCircularDeep
										className={styles.border}
										rotation={0}
										fill={
											sombreBannerTheme
												? Colours.MIDNIGHT
												: Colours.SUNSHINE_YELLOW
										}
									/>
								</div>
							</div>
						)}

						{mediaVariant === "video" && (
							<div
								className={cx(styles.herowithvideocontainer, {
									[styles.sombreTheme]: sombreBannerTheme,
								})}
							>
								<YouTubeLite
									title={`${title} introductory video`}
									youtubeId={videoId}
									poster={"maxresdefault"}
								/>
							</div>
						)}

						<div className={styles.introcontent}>
							<header className={styles.textcontainer}>
								<h1>{title}</h1>
								<p>{text}</p>
								{ctaTitle && ctaUrl && (
									<Button
										className={styles.cta}
										url={ctaUrl}
										variant={
											sombreBannerTheme ? "reversetertiary" : "secondary"
										}
									>
										{ctaTitle}
									</Button>
								)}
							</header>
							{contentVariant === "links" && (
								<div className={styles.linkscontainer}>
									{linkItems.map((quickLinks) => (
										<QuickLinksEmbeddedItem key={quickLinks.title} {...quickLinks} />
									))}
								</div>
							)}
							{contentVariant === "widget" && DesktopWidget}
						</div>
					</div>
				</div>
				<div
					className={cx(styles.fuzzyborder, {
						[styles.sombreTheme]: sombreBannerTheme,
					})}
				>
					<FuzzyBorderFullWidth rotation={0} fill={Colours.SANDY_FUR} />
				</div>
			</div>
		</>
	);
};

export default IntroHomepageComponent;
